import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import TableFilter from "src/components/TableFilter";
import DateUtils from "src/services/utils/DateUtils";
import CommonService from "src/modules/data-protection/apis/CommonService";

const FilteringComponent = ({
    t,
    onChange
}) => {
    const [tableFilters, setTableFilters] = useState({
        fromDate: null,
        toDate: null,
        owner: null,
        responsiblePerson: null,
        activityTitle: null,
        softwareName: null,
        action: null
    });

    const dateUtils = new DateUtils();

    const handleFetchUsersQuery = useQuery({
        queryKey: ["data-protection-users-list"],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchAnalystAndAdminAnalystList();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching users list."), {
                type: "error",
            });
        },
    });

    const historyLogActions = useMemo(() => {
        return [
            {
                id: 'create_processing_activity',
                label: 'Created processing activity'
            },
            {
                id: 'change_processing_activity',
                label: 'Changed processing activity'
            },
            {
                id: 'remove_processing_activity',
                label: 'Removed processing activity'
            },
            {
                id: 'update_processing_activity_module_name',
                label: 'Updated module name'
            },
            {
                id: 'add_processing_activity_module_name',
                label: 'Added module name'
            },
            {
                id: 'update_responsible_person',
                label: 'Updated responsible person'
            },
            {
                id: 'change_analyst',
                label: 'Changed analyst'
            }
        ]
    }, [t]);

    const tableFilterTabs = useMemo(() => {
        return [
            {
                type: 'date',
                name: 'filter-date',
                title: 'Date',
                icon: <i className="ri-calendar-event-line" />,
                defaultValue: {
                    from: tableFilters['createdAt[from]'],
                    to: tableFilters['createdAt[to]']
                }
            },
            {
                type: 'radio_list',
                name: 'owner',
                title: 'Assigned Analyst',
                icon: <i className="ri-user-search-line" />,
                isLoading: handleFetchUsersQuery.isFetching || handleFetchUsersQuery.isLoading,
                defaultValue: tableFilters?.owner,
                data: handleFetchUsersQuery.data || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return `${item.first_name} ${item.last_name}` 
                }
            },
            {
                type: 'radio_list',
                name: 'responsiblePerson',
                title: 'Responsible Person',
                icon: <i className="ri-user-line" />,
                isLoading: handleFetchUsersQuery.isFetching || handleFetchUsersQuery.isLoading,
                defaultValue: tableFilters?.responsiblePerson,
                data: handleFetchUsersQuery.data || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return `${item.first_name} ${item.last_name}` 
                }
            },
            {
                type: 'search_textbox',
                name: 'softwareName',
                title: t('Software Name'),
                icon: <i className="ri-terminal-box-line" />,
                defaultValue: tableFilters?.softwareName,
                options: {
                    placeholder: t('Search in software names'),
                    typingDelay: 750
                },
                displayIndependently: true,
                onFilterChange: (value) => {
                    setTableFilters((currentState) => ({...currentState, softwareName: value}))
                }
            },
            {
                type: 'search_textbox',
                name: 'activityTitle',
                title: t('Activity Title'),
                icon: <i className="ri-task-line" />,
                defaultValue: tableFilters?.activityTitle,
                options: {
                    placeholder: t('Search in activity titles')
                }
            },
            {
                type: 'radio_list',
                name: 'action',
                title: 'Actions',
                icon: <i className="ri-run-line" />,
                dtypingDelayefaultValue: tableFilters?.action,
                data: historyLogActions,
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.label
                }
            }
        ]
    }, [
        tableFilters,
        handleFetchUsersQuery,
        historyLogActions,
        t
    ]);

    const onTableFilterOkButtonClicked = (filters, independentTabsData) => {
        if (filters) {
            const result = {}

            if ('filter-date' in filters) {
                result['fromDate'] = dateUtils.convertDateToDate(
                    new Date(filters['filter-date']?.from),
                    'YYYY-MM-DD 00:00:00'
                )

                result['toDate'] = dateUtils.convertDateToDate(
                    new Date(filters['filter-date']?.to),
                    'YYYY-MM-DD 23:59:59'
                )
            }

            if ('owner' in filters) {
                result.owner = filters.owner !== 'all' ? filters.owner : undefined
            }

            if ('responsiblePerson' in filters) {
                result.responsiblePerson = filters.responsiblePerson !== 'all' ? filters.responsiblePerson : undefined
            }

            if('softwareName' in filters) {
                result.softwareName = filters.softwareName || undefined;
            }

            if('activityTitle' in filters) {
                result.activityTitle = filters.activityTitle || undefined;
            }

            if ('action' in filters) {
                result.action = filters.action !== 'all' ? filters.action : undefined
            }
            
            setTableFilters({
                ...tableFilters,
                ...result
            })
        } else {
            setTableFilters({
                fromDate: null,
                toDate: null,
                owner: null,
                responsiblePerson: null,
                activityTitle: null,
                action: null,
                softwareName: independentTabsData?.softwareName
            })
        }
    }

    useEffect(() => {
        onChange && onChange(tableFilters)
    }, [ 
        tableFilters,
        onChange 
    ]);

    return (
        <TableFilter
            className="me-2"
            defaultActiveTab='filter-date'
            tabs={tableFilterTabs}
            onOkButtonClicked={
                onTableFilterOkButtonClicked
            }
        />
    )
}

export default withNamespaces()(FilteringComponent);